import React, { useState } from 'react';
import api, { handleAPIError } from '../../services/api';

interface SERPResult {
  position: number;
  title: string;
  url: string;
  domain: string;
  description: string;
  isTarget: boolean;
}

interface EngineData {
  organicRank: number | null;
  localPackRank: number | null;
  localPackTotal: number | null;
  results: SERPResult[];
  error?: string;
}

interface SERPCheckerResponse {
  keyword: string;
  location: string;
  domain: string;
  engines: {
    google: EngineData;
    bing: EngineData;
    yahoo: EngineData;
  };
}

type EngineName = 'google' | 'bing' | 'yahoo';

const engineLabels: Record<EngineName, string> = {
  google: 'Google',
  bing: 'Bing',
  yahoo: 'Yahoo',
};

const LoadingSpinner: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12 gap-3">
    <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
    <p className="text-sm text-gray-500">Checking SERP rankings... This may take a moment.</p>
  </div>
);

const RankBadge: React.FC<{ rank: number | null; label: string }> = ({ rank, label }) => {
  const found = rank !== null && rank > 0;
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <span
        className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
          found
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-700'
        }`}
      >
        {found ? `#${rank}` : 'Not Found'}
      </span>
    </div>
  );
};

const EngineCard: React.FC<{ name: EngineName; data: EngineData }> = ({ name, data }) => {
  if (data.error) {
    return (
      <div className="border border-red-200 rounded-lg overflow-hidden">
        <div className="bg-red-50 px-4 py-3 border-b border-red-200">
          <h3 className="text-lg font-semibold text-red-800">{engineLabels[name]}</h3>
        </div>
        <div className="px-4 py-4 text-sm text-red-600">{data.error}</div>
      </div>
    );
  }

  const top10 = data.results.slice(0, 10);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-semibold text-gray-800">{engineLabels[name]}</h3>
        <div className="flex gap-6">
          <RankBadge rank={data.organicRank} label="Organic Rank" />
          {data.localPackRank !== null && (
            <RankBadge rank={data.localPackRank} label="Local Pack" />
          )}
        </div>
      </div>

      {top10.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  #
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Domain
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {top10.map((result) => (
                <tr
                  key={`${name}-${result.position}`}
                  className={`${
                    result.isTarget ? 'bg-blue-50' : 'hover:bg-gray-50'
                  } transition-colors`}
                >
                  <td className="px-4 py-2 text-sm text-gray-700 font-mono font-semibold">
                    {result.position}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate" title={result.title}>
                    {result.isTarget && (
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0" />
                    )}
                    {result.title}
                  </td>
                  <td className="px-4 py-2 text-sm text-blue-600 max-w-sm truncate" title={result.url}>
                    <a href={result.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {result.url}
                    </a>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {result.domain}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-4 py-4 text-sm text-gray-500 text-center">No results available.</div>
      )}
    </div>
  );
};

const SERPRankChecker: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [domain, setDomain] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SERPCheckerResponse | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedKeyword = keyword.trim();
    const trimmedDomain = domain.trim();
    if (!trimmedKeyword || !trimmedDomain) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await api.post<SERPCheckerResponse>('/seotools/serp-checker', {
        keyword: trimmedKeyword,
        domain: trimmedDomain,
        location: location.trim() || undefined,
      });
      setData(response.data);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const engineOrder: EngineName[] = ['google', 'bing', 'yahoo'];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">SERP Rank Checker</h2>
        <p className="text-sm text-gray-500 mt-1">
          Check where a domain ranks for a keyword across Google, Bing, and Yahoo.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="serp-keyword" className="block text-sm font-medium text-gray-700 mb-1">
              Keyword <span className="text-red-500">*</span>
            </label>
            <input
              id="serp-keyword"
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g. plumber near me"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="serp-domain" className="block text-sm font-medium text-gray-700 mb-1">
              Domain <span className="text-red-500">*</span>
            </label>
            <input
              id="serp-domain"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="e.g. example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="serp-location" className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="serp-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. New York, United States"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
        </div>
        <div>
          <button
            type="submit"
            disabled={loading || !keyword.trim() || !domain.trim()}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Checking...' : 'Check Rankings'}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading && <LoadingSpinner />}

      {data && !loading && (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700">
            Results for <strong className="text-gray-900">"{data.keyword}"</strong>
            {data.location && (
              <> in <strong className="text-gray-900">{data.location}</strong></>
            )}
            {' '} &mdash; target domain: <strong className="text-blue-700">{data.domain}</strong>
          </div>

          {engineOrder.map((engine) => (
            <EngineCard key={engine} name={engine} data={data.engines[engine]} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SERPRankChecker;
